<template>
    <div class="import-wizard-container">
        <v-row v-if="previewData" style="height: max-content" class="ma-0 pb-offset flex-nowrap">
            <!-- Colonna Wizard -->
            <v-col
                cols="12"
                :class="['pa-1', 'wizard-column', { 'with-pdf-panel': pdfPanelExpanded }]"
            >
                <v-card class="pa-0 background-2 round-card">
                    <v-card-title class="pa-3">
                        <v-btn icon class="mr-2" @click="goBack">
                            <v-icon>mdi-arrow-left</v-icon>
                        </v-btn>
                        <p class="pl-3 mb-0">Importa Documento Elettronico</p>
                        <v-spacer></v-spacer>
                        <!-- Toggle PDF Preview -->
                        <v-tooltip left>
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    icon
                                    v-on="on"
                                    @click="togglePdfPanel"
                                    :color="pdfPanelExpanded ? 'primary' : ''"
                                >
                                    <v-icon>{{ pdfPanelExpanded ? 'mdi-chevron-right' : 'mdi-file-eye-outline' }}</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ pdfPanelExpanded ? 'Nascondi anteprima' : 'Mostra anteprima PDF' }}</span>
                        </v-tooltip>
                    </v-card-title>
                    <v-divider></v-divider>

                    <!-- Alert per documenti non importabili -->
                    <v-alert v-if="!previewData.importable" type="warning" class="ma-0">
                        <div class="font-weight-bold">{{ getTypeInfoName }}</div>
                        <div>{{ previewData.reason }}</div>
                    </v-alert>

                    <v-card-text v-else>
                    <!-- Alert per documenti già importati -->
                    <v-alert v-if="previewData.already_imported" type="warning" text class="mb-4">
                        <div class="d-flex align-center">
                            <v-icon color="warning" class="mr-3">mdi-alert-circle</v-icon>
                            <div class="flex-grow-1">
                                <div class="font-weight-bold">Documento già importato</div>
                                <div v-if="previewData.previous_import">
                                    Importato come {{ getPreviousImportTypeName() }}
                                    <span v-if="previewData.previous_import.document_number">
                                        n. {{ previewData.previous_import.document_number }}
                                    </span>
                                    <span v-if="previewData.previous_import.document_date">
                                        del {{ formatDate(previewData.previous_import.document_date) }}
                                    </span>
                                </div>
                                <div class="text-caption mt-1">Reimportando, il documento precedente viene eliminato (se non ha pagamenti registrati).</div>
                            </div>
                            <v-btn
                                v-if="previewData.previous_import && previewData.previous_import.document_id"
                                small
                                outlined
                                color="primary"
                                class="ml-3"
                                @click="goToPreviousImport"
                            >
                                <v-icon left small>mdi-open-in-new</v-icon>
                                Vai al documento
                            </v-btn>
                        </div>
                    </v-alert>

                    <!-- Riepilogo documento -->
                    <v-card outlined class="mb-4 background-1">
                        <v-card-subtitle class="font-weight-bold">
                            <v-icon small class="mr-2">mdi-file-document-outline</v-icon>
                            Documento: {{ edocumentNumber }} del {{ formatDate(edocumentDate) }}
                        </v-card-subtitle>
                        <v-card-text>
                            <v-chip small :color="getTypeColor(getTypeInfoTarget)" class="mr-2">
                                {{ getTypeInfoName }}
                            </v-chip>
                            <v-chip small outlined class="mr-2">
                                {{ edocumentType }}
                            </v-chip>
                            <span class="font-weight-bold ml-2">
                                Totale: {{ formatCurrency(edocumentTotal) }}
                            </span>
                        </v-card-text>
                    </v-card>

                    <!-- Stepper -->
                    <v-stepper v-model="step" vertical class="elevation-0" outlined>

                        <!-- STEP 1: Fornitore -->
                        <v-stepper-step :complete="step > 1" step="1">
                            Fornitore
                            <small>{{ supplierSummary }}</small>
                        </v-stepper-step>

                        <v-stepper-content step="1">
                            <v-card outlined class="mb-5">
                                <v-card-text>
                                    <!-- Dati dal documento -->
                                    <v-alert type="info" text dense class="mb-4">
                                        <div class="font-weight-bold">Dati dal documento elettronico:</div>
                                        <div>{{ supplierFromEdocument.business_name }}</div>
                                        <div v-if="supplierFromEdocument.vat_number">
                                            P.IVA: {{ supplierFromEdocument.vat_number }}
                                        </div>
                                        <div v-if="supplierFromEdocument.fiscal_code">
                                            C.F.: {{ supplierFromEdocument.fiscal_code }}
                                        </div>
                                    </v-alert>

                                    <!-- Fornitore trovato -->
                                    <v-alert v-if="supplierMatched" type="success" text dense class="mb-4">
                                        <div class="font-weight-bold">Fornitore trovato in anagrafica:</div>
                                        <div>{{ supplierMatched.business_name }}</div>
                                    </v-alert>

                                    <!-- Fornitore mancante con creazione automatica attiva nelle impostazioni -->
                                    <v-alert v-if="autoCreateSupplier && !selectedSupplier" type="info" text dense class="mb-4">
                                        Fornitore non presente in anagrafica: premendo Continua viene creato con i dati dell'XML.
                                    </v-alert>

                                    <!-- Selezione fornitore -->
                                    <async-autocomplete
                                        v-model="selectedSupplier"
                                        async-url="async-suppliers"
                                        :items="supplierInitialItems"
                                        :search-keys="['subtitle']"
                                        label="Seleziona o cerca fornitore *"
                                        placeholder="Cerca per ragione sociale, P.IVA o C.F."
                                        return-object
                                        :rules="[v => !!v || 'Seleziona un fornitore']"
                                    ></async-autocomplete>

                                    <!-- Crea nuovo fornitore -->
                                    <v-btn
                                        v-if="!supplierMatched"
                                        color="primary"
                                        outlined
                                        small
                                        class="mt-2"
                                        @click="showCreateSupplierDialog = true"
                                    >
                                        <v-icon left small>mdi-plus</v-icon>
                                        Crea nuovo fornitore
                                    </v-btn>
                                </v-card-text>
                            </v-card>

                            <v-btn color="primary" @click="nextStep(1)" :disabled="!selectedSupplier && !autoCreateSupplier" :loading="creatingSupplier">
                                Continua
                                <v-icon right>mdi-arrow-right</v-icon>
                            </v-btn>
                        </v-stepper-content>

                        <!-- STEP 2: Prodotti -->
                        <v-stepper-step :complete="step > 2" step="2">
                            Prodotti
                            <small>{{ productsSummary }}</small>
                        </v-stepper-step>

                        <v-stepper-content step="2">
                            <v-card outlined class="mb-5">
                                <v-card-text>
                                    <!-- Validation Alert -->
                                    <v-alert
                                        v-if="linesWithErrorsCount > 0"
                                        type="warning"
                                        text
                                        dense
                                        class="mb-3"
                                    >
                                        <strong>{{ linesWithErrorsCount }}</strong> {{ linesWithErrorsCount === 1 ? 'riga ha' : 'righe hanno' }} dati mancanti (descrizione, quantità, prezzo, IVA o prodotto).
                                        Completa tutti i campi obbligatori per procedere.
                                    </v-alert>

                                    <!-- Bulk Actions Toolbar -->
                                    <v-toolbar v-if="selectedLineIndices.length > 0" dense flat color="primary" dark class="mb-3 rounded">
                                        <v-toolbar-title class="text-body-2">
                                            {{ selectedLineIndices.length }} righe selezionate
                                        </v-toolbar-title>
                                        <v-spacer></v-spacer>
                                        <v-btn small outlined class="mr-2" @click="showBulkVatDialog = true">
                                            <v-icon left small>mdi-percent</v-icon>
                                            Imposta IVA
                                        </v-btn>
                                        <v-btn small outlined class="mr-2" @click="showBulkProductDialog = true">
                                            <v-icon left small>mdi-package-variant</v-icon>
                                            Imposta Prodotto
                                        </v-btn>
                                        <v-btn small outlined class="mr-2" @click="showBulkDescriptionDialog = true">
                                            <v-icon left small>mdi-text</v-icon>
                                            Imposta Descrizione
                                        </v-btn>
                                        <v-btn small outlined @click="bulkToggleProductSupplier">
                                            <v-icon left small>mdi-link-variant</v-icon>
                                            Associa Prod-Forn
                                        </v-btn>
                                    </v-toolbar>

                                    <!-- aliquota/natura dell'XML senza codice IVA in anagrafica:
                                         niente preselezione sbagliata, si segnala e si puo' creare -->
                                    <v-alert
                                        v-for="pair in unresolvedVats"
                                        :key="pair.label"
                                        type="warning"
                                        text
                                        dense
                                        class="mb-3 unresolved-vat"
                                    >
                                        <div class="d-flex flex-wrap align-center" style="gap: 12px">
                                            <span>
                                                Nessun codice IVA per <strong>{{ pair.label }}</strong>:
                                                {{ pair.lines === 1 ? '1 riga è rimasta' : pair.lines + ' righe sono rimaste' }} senza IVA.
                                                Scegli il codice corretto nella colonna IVA
                                                <template v-if="pair.proposal">oppure crealo</template>.
                                            </span>
                                            <v-spacer></v-spacer>
                                            <v-btn v-if="pair.proposal" small color="primary" depressed @click="openVatDialog(pair)">
                                                <v-icon left small>mdi-plus</v-icon>
                                                Crea codice IVA
                                            </v-btn>
                                        </div>
                                    </v-alert>

                                    <!-- larghezze minime: con poco spazio la tabella scorre in orizzontale
                                         invece di schiacciare i campi fino a renderli illeggibili -->
                                    <v-simple-table dense class="lines-table">
                                        <template v-slot:default>
                                            <thead>
                                                <tr>
                                                    <th style="width: 40px">
                                                        <v-checkbox
                                                            :input-value="allLinesSelected"
                                                            :indeterminate="someLinesSelected && !allLinesSelected"
                                                            @change="toggleSelectAll"
                                                            hide-details
                                                            class="mt-0 pt-0"
                                                        ></v-checkbox>
                                                    </th>
                                                    <th style="width: 50px">#</th>
                                                    <th class="col-description">Descrizione</th>
                                                    <th class="col-quantity" style="width: 100px">Q.tà</th>
                                                    <th class="col-price" style="width: 130px">Prezzo</th>
                                                    <th class="col-vat" style="width: 190px">IVA</th>
                                                    <th class="col-product" style="width: 350px">Prodotto</th>
                                                    <th style="width: 80px">Azioni</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(line, index) in productLines" :key="line.id" :class="{ 'selected-row': isLineSelected(index), 'error-row': hasLineErrors(index) }">
                                                    <td>
                                                        <v-checkbox
                                                            :input-value="isLineSelected(index)"
                                                            @change="toggleLineSelection(index)"
                                                            hide-details
                                                            class="mt-0 pt-0"
                                                        ></v-checkbox>
                                                    </td>
                                                    <td>
                                                        {{ line.line_number }}
                                                        <v-icon v-if="hasLineErrors(index)" small color="error" class="ml-1">mdi-alert-circle</v-icon>
                                                    </td>
                                                    <td>
                                                        <v-text-field
                                                            v-model="line.name"
                                                            dense
                                                            hide-details
                                                            outlined
                                                            :error="!line.name || line.name.trim() === ''"
                                                            class="my-1"
                                                        ></v-text-field>
                                                    </td>
                                                    <td>
                                                        <!-- righe descrittive: quantita', prezzo e IVA non si compilano -->
                                                        <span v-if="line.is_description" class="grey--text">—</span>
                                                        <v-text-field
                                                            v-else
                                                            v-model.number="line.quantity"
                                                            type="number"
                                                            dense
                                                            hide-details
                                                            outlined
                                                            :error="!line.quantity || line.quantity <= 0"
                                                            class="my-1"
                                                        ></v-text-field>
                                                    </td>
                                                    <td>
                                                        <span v-if="line.is_description" class="grey--text">—</span>
                                                        <v-text-field
                                                            v-else
                                                            v-model.number="line.unit_price"
                                                            type="number"
                                                            step="0.01"
                                                            dense
                                                            hide-details
                                                            outlined
                                                            :error="line.unit_price === null || line.unit_price === undefined"
                                                            suffix="€"
                                                            class="my-1"
                                                        ></v-text-field>
                                                        <small v-if="!line.is_description && discountLabel(line)" class="d-block grey--text">
                                                            sconti XML: {{ discountLabel(line) }}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <span v-if="line.is_description" class="grey--text">—</span>
                                                        <v-select
                                                            v-else
                                                            v-model="line.vat"
                                                            :items="vatCodeOptions"
                                                            item-text="text"
                                                            item-value="key"
                                                            dense
                                                            hide-details
                                                            outlined
                                                            :disabled="line.is_description"
                                                            :menu-props="{'offset-y': true}"
                                                            :error="!line.is_description && (!line.vat || line.vat.trim() === '')"
                                                            class="my-1"
                                                        ></v-select>
                                                        <small v-if="line.vat_unresolved && !line.vat" class="d-block warning--text">
                                                            {{ line.vat_label }} senza codice
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <v-chip v-if="line.is_description" small outlined color="info" class="mb-1">
                                                            <v-icon left small>mdi-text-box-outline</v-icon>
                                                            Descrizione
                                                        </v-chip>
                                                        <async-autocomplete
                                                            v-if="!line.is_description"
                                                            v-model="line.product"
                                                            async-url="async-products"
                                                            item-text="text"
                                                            item-value="value"
                                                            :items="line.product ? [line.product] : []"
                                                            :search-keys="['code', 'subtitle']"
                                                            placeholder="Cerca prodotto..."
                                                            return-object
                                                            hide-details
                                                            :disabled="line.is_description"
                                                            :error="!line.is_description && !line.product"
                                                            class="my-1"
                                                            selection-max-width="250px"
                                                        ></async-autocomplete>
                                                    </td>
                                                    <td>
                                                        <v-tooltip v-if="line.create_product_supplier" top>
                                                            <template v-slot:activator="{ on }">
                                                                <v-icon small color="info" v-on="on" class="mr-1">mdi-link-variant</v-icon>
                                                            </template>
                                                            <span>Associazione prodotto-fornitore attiva</span>
                                                        </v-tooltip>
                                                        <!-- righe descrittive: nel dialog ci sarebbero solo campi ignorati,
                                                             la descrizione si modifica gia' nella riga -->
                                                        <v-tooltip top v-if="!line.is_description">
                                                            <template v-slot:activator="{ on }">
                                                                <v-btn
                                                                    icon
                                                                    small
                                                                    v-on="on"
                                                                    @click="openLineDetails(index)"
                                                                >
                                                                    <v-icon small>mdi-pencil</v-icon>
                                                                </v-btn>
                                                            </template>
                                                            <span>Modifica dettagli</span>
                                                        </v-tooltip>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </template>
                                    </v-simple-table>
                                </v-card-text>
                            </v-card>

                            <v-btn color="primary" @click="nextStep(2)" :disabled="!isProductsStepValid">
                                Continua
                                <v-icon right>mdi-arrow-right</v-icon>
                            </v-btn>

                            <v-btn text @click="step = 1" class="ml-2">
                                <v-icon left>mdi-arrow-left</v-icon>
                                Indietro
                            </v-btn>
                        </v-stepper-content>

                        <!-- STEP 3: Conferma -->
                        <v-stepper-step :complete="step > 3" step="3">
                            Conferma
                            <small v-if="form.subtype">{{ getFieldLabel('subtype', form.subtype) }}</small>
                        </v-stepper-step>

                        <v-stepper-content step="3">
                            <v-card outlined class="mb-5">
                                <v-card-text>
                                    <!-- Dynamic fields from backend -->
                                    <fields-generator
                                        v-if="fieldsset"
                                        :fieldsset.sync="fieldsset"
                                        :model.sync="form"
                                        ref="fieldsgenerator"
                                        class="pt-4 px-0"
                                        @loaded="applyDefaults"
                                    ></fields-generator>

                                    <!-- da dove arriva la condizione di pagamento proposta -->
                                    <v-alert
                                        v-if="paymentTermStatus"
                                        text
                                        dense
                                        :type="paymentTermStatus.type"
                                        class="mb-4 body-2 payment-term-status"
                                    >{{ paymentTermStatus.text }}</v-alert>

                                    <!-- Scadenze lette dall'XML: servono a scegliere la condizione di
                                         pagamento quando non viene riconosciuta in automatico -->
                                    <div class="font-weight-bold mb-2">Scadenze dall'XML</div>
                                    <v-simple-table v-if="xmlPayments.length" dense class="mb-2 xml-payments">
                                        <thead>
                                            <tr>
                                                <th>Scadenza</th>
                                                <th>Metodo di pagamento</th>
                                                <th class="text-right">Importo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="payment in xmlPayments" :key="payment.id">
                                                <td>{{ payment.expire_date ? formatDate(payment.expire_date) : '—' }}</td>
                                                <td>{{ paymentMethodLabel(payment) }}</td>
                                                <td class="text-right">{{ formatCurrency(payment.expire_amount) }}</td>
                                            </tr>
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colspan="2" class="text-right font-weight-bold">Totale</td>
                                                <td class="text-right font-weight-bold">{{ formatCurrency(xmlPaymentsTotal) }}</td>
                                            </tr>
                                        </tfoot>
                                    </v-simple-table>
                                    <small class="d-block grey--text mb-4">
                                        {{ xmlPayments.length
                                            ? 'Il documento avrà queste rate. Se la condizione di pagamento scelta non le riproduce, le rate vengono salvate come modificate a mano.'
                                            : 'L\'XML non contiene scadenze: le rate verranno calcolate dalla condizione di pagamento scelta.' }}
                                    </small>

                                    <v-divider class="my-4"></v-divider>

                                    <!-- Riepilogo -->
                                    <div class="font-weight-bold mb-3">Riepilogo importazione</div>

                                    <v-simple-table dense class="mb-4">
                                        <template v-slot:default>
                                            <tbody>
                                                <tr>
                                                    <td class="font-weight-bold" width="200">Tipo documento</td>
                                                    <td>{{ getTargetModelName() }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="font-weight-bold">Fornitore</td>
                                                    <td>{{ selectedSupplierName }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="font-weight-bold">Documento origine</td>
                                                    <td>{{ edocumentType }} n. {{ edocumentNumber }} del {{ formatDate(edocumentDate) }}</td>
                                                </tr>
                                                <tr v-for="field in summaryFields" :key="field.key">
                                                    <td class="font-weight-bold">{{ field.label }}</td>
                                                    <td>{{ getSummaryValue(field) }}</td>
                                                </tr>
                                            </tbody>
                                        </template>
                                    </v-simple-table>

                                    <div class="font-weight-bold mb-3">Righe ({{ productLines.length }})</div>
                                    <v-simple-table dense>
                                        <thead>
                                            <tr>
                                                <th>Descrizione</th>
                                                <th class="text-right">Q.tà</th>
                                                <th class="text-right">Prezzo</th>
                                                <th class="text-right">Sconti</th>
                                                <th class="text-right">IVA</th>
                                                <th class="text-right">Totale</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="line in productLines" :key="line.id">
                                                <td>{{ line.name }}</td>
                                                <td class="text-right">{{ line.quantity }}</td>
                                                <td class="text-right">{{ formatCurrency(line.unit_price) }}</td>
                                                <td class="text-right">{{ discountLabel(line) || '—' }}</td>
                                                <td class="text-right">{{ getVatLabel(line.vat) }}</td>
                                                <td class="text-right">{{ formatCurrency(lineTotal(line)) }}</td>
                                            </tr>
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colspan="5" class="text-right font-weight-bold">Totale</td>
                                                <td class="text-right font-weight-bold">{{ formatCurrency(calculatedTotal) }}</td>
                                            </tr>
                                        </tfoot>
                                    </v-simple-table>
                                </v-card-text>
                            </v-card>

                            <v-btn
                                color="success"
                                @click="executeImport"
                                :loading="importing"
                                :disabled="!isConfirmStepValid"
                            >
                                <v-icon left>mdi-check</v-icon>
                                Importa Documento
                            </v-btn>

                            <v-btn text @click="step = 2" class="ml-2">
                                <v-icon left>mdi-arrow-left</v-icon>
                                Indietro
                            </v-btn>
                        </v-stepper-content>
                    </v-stepper>
                </v-card-text>
            </v-card>
        </v-col>

        <!-- Colonna Anteprima PDF con slide laterale -->
        <transition name="slide-pdf" @before-enter="pdfPanelReady = false" @after-enter="pdfPanelReady = true">
            <v-col v-if="pdfPanelExpanded" cols="5" class="pa-1 pdf-panel-col d-none d-lg-block">
                <v-card class="pdf-preview-card round-card background-2" outlined>
                    <v-card-title class="pa-3">
                        <v-icon class="mr-2">mdi-file-pdf-box</v-icon>
                        <span>Anteprima Documento</span>
                        <v-spacer></v-spacer>
                        <v-btn icon small @click="downloadPdf" :disabled="!pdfData">
                            <v-icon>mdi-download</v-icon>
                        </v-btn>
                        <v-btn icon small class="ml-1" @click="togglePdfPanel">
                            <v-icon>mdi-chevron-right</v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="pa-0 background-2">
                        <v-progress-linear v-if="loadingPdf" indeterminate></v-progress-linear>
                        <object
                            v-else-if="pdfData && pdfPanelReady"
                            :data="pdfData"
                            type="application/pdf"
                            class="pdf-object"
                            title="Anteprima Documento Elettronico"
                        >
                            <p class="pa-4">
                                Il tuo browser non supporta la visualizzazione PDF inline.
                                <a :href="pdfData" target="_blank">Clicca qui per aprire il PDF</a>
                            </p>
                        </object>
                        <v-alert v-else-if="!pdfData" type="error" text class="ma-4">
                            Impossibile caricare l'anteprima PDF
                        </v-alert>
                    </v-card-text>
                </v-card>
            </v-col>
        </transition>

        <!-- Dialog crea fornitore -->
        <v-dialog v-model="showCreateSupplierDialog" max-width="800" persistent>
            <v-card class="background-2 round-card">
                <v-card-title class="pa-3">
                    <p class="pl-2 pl-xl-3 mb-0">Crea nuovo Fornitore</p>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="showCreateSupplierDialog = false"><v-icon>close</v-icon></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-4">
                    <v-text-field
                        v-model="newSupplier.business_name"
                        label="Ragione sociale *"
                        outlined
                        dense
                        :rules="[v => !!v || 'Campo obbligatorio']"
                    ></v-text-field>
                    <v-row>
                        <v-col cols="6">
                            <v-text-field
                                v-model="newSupplier.vat_number"
                                label="Partita IVA"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="newSupplier.fiscal_code"
                                label="Codice Fiscale"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <v-text-field
                        v-model="newSupplier.legal_address_street"
                        label="Indirizzo"
                        outlined
                        dense
                    ></v-text-field>
                    <v-row>
                        <v-col cols="4">
                            <v-text-field
                                v-model="newSupplier.legal_address_zipcode"
                                label="CAP"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="5">
                            <v-text-field
                                v-model="newSupplier.legal_address_city"
                                label="Città"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="3">
                            <v-text-field
                                :value="newSupplier.province_initial || ''"
                                label="Prov."
                                outlined
                                dense
                                disabled
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="newSupplier.primary_telephone"
                                label="Telefono"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="newSupplier.primary_email"
                                label="Email"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="info" @click="showCreateSupplierDialog = false">Annulla</v-btn>
                    <v-btn text color="primary" @click="createSupplier" :loading="creatingSupplier">Crea Fornitore</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <vat-code-dialog
            v-model="vatDialog.open"
            :proposal="vatDialog.proposal"
            :label="vatDialog.label"
            :types="previewData.vat_types || []"
            @created="applyCreatedVat"
        ></vat-code-dialog>

        <!-- Dialog dettagli riga -->
        <v-dialog v-model="showLineDetailsDialog" max-width="600" persistent>
            <v-card v-if="editingLine" class="background-2 round-card">
                <v-card-title class="pa-3">
                    <p class="pl-2 pl-xl-3 mb-0">Dettagli Riga #{{ editingLine.line_number }}</p>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="showLineDetailsDialog = false"><v-icon>close</v-icon></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-4">
                    <v-text-field
                        v-model="editingLine.name"
                        label="Descrizione"
                        outlined
                        dense
                    ></v-text-field>
                    <v-row>
                        <v-col cols="6">
                            <v-text-field
                                v-model.number="editingLine.quantity"
                                label="Quantità"
                                type="number"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="editingLine.unit"
                                label="Unità di misura"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="6">
                            <v-text-field
                                v-model.number="editingLine.unit_price"
                                label="Prezzo unitario"
                                type="number"
                                step="0.01"
                                suffix="€"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-select
                                v-model="editingLine.vat"
                                :items="vatCodeOptions"
                                item-text="text"
                                item-value="key"
                                label="Aliquota IVA"
                                outlined
                                dense
                                :menu-props="{'offset-y': true}"
                            ></v-select>
                        </v-col>
                    </v-row>
                    <v-divider class="my-3"></v-divider>
                    <div class="text-caption mb-2">Codice prodotto originale</div>
                    <div>{{ editingLine.product_code }} ({{ editingLine.product_code_type }})</div>

                    <v-checkbox
                        v-if="editingLine.product"
                        v-model="editingLine.create_product_supplier"
                        label="Crea associazione prodotto-fornitore con questo codice"
                        hide-details
                        class="mt-3"
                    ></v-checkbox>

                    <v-checkbox
                        v-if="!editingLine.product"
                        v-model="editingLine.create_product"
                        label="Crea nuovo prodotto da questa riga"
                        hide-details
                        class="mt-3"
                    ></v-checkbox>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="info" @click="showLineDetailsDialog = false">Chiudi</v-btn>
                    <v-btn text color="primary" @click="closeLineDetails">Salva</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog imposta IVA massiva -->
        <v-dialog v-model="showBulkVatDialog" max-width="500" persistent>
            <v-card class="background-2 round-card">
                <v-card-title class="pa-3">
                    <p class="pl-2 pl-xl-3 mb-0">Imposta IVA</p>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="showBulkVatDialog = false"><v-icon>close</v-icon></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-4">
                    <p class="mb-4">Seleziona l'aliquota IVA da assegnare alle {{ selectedLineIndices.length }} righe selezionate:</p>
                    <v-autocomplete
                        v-model="bulkVat"
                        :items="vatCodeOptions"
                        item-text="text"
                        item-value="key"
                        label="Cerca aliquota IVA..."
                        outlined
                        dense
                        clearable
                        :menu-props="{'offset-y': true}"
                    >
                        <template v-slot:item="{ item }">
                            <v-list-item-content>
                                <v-list-item-title>{{ item.text }}</v-list-item-title>
                            </v-list-item-content>
                        </template>
                    </v-autocomplete>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="info" @click="showBulkVatDialog = false">Annulla</v-btn>
                    <v-btn text color="primary" @click="applyBulkVat" :disabled="!bulkVat">Applica</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog imposta prodotto massivo -->
        <v-dialog v-model="showBulkProductDialog" max-width="500" persistent>
            <v-card class="background-2 round-card">
                <v-card-title class="pa-3">
                    <p class="pl-2 pl-xl-3 mb-0">Imposta Prodotto</p>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="showBulkProductDialog = false"><v-icon>close</v-icon></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-4">
                    <p class="mb-4">Seleziona il prodotto da assegnare alle {{ selectedLineIndices.length }} righe selezionate:</p>
                    <async-autocomplete
                        v-model="bulkProduct"
                        async-url="async-products"
                        item-text="text"
                        item-value="value"
                        :search-keys="['code', 'subtitle']"
                        label="Cerca prodotto..."
                        return-object
                    ></async-autocomplete>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="info" @click="showBulkProductDialog = false">Annulla</v-btn>
                    <v-btn text color="primary" @click="bulkSetProduct" :disabled="!bulkProduct">Applica</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog imposta descrizione massiva -->
        <v-dialog v-model="showBulkDescriptionDialog" max-width="500" persistent>
            <v-card class="background-2 round-card">
                <v-card-title class="pa-3">
                    <p class="pl-2 pl-xl-3 mb-0">Imposta Descrizione</p>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="showBulkDescriptionDialog = false"><v-icon>close</v-icon></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-4">
                    <p class="mb-4">Inserisci la descrizione da assegnare alle {{ selectedLineIndices.length }} righe selezionate:</p>
                    <v-text-field
                        v-model="bulkDescription"
                        label="Descrizione"
                        outlined
                        dense
                    ></v-text-field>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="info" @click="showBulkDescriptionDialog = false">Annulla</v-btn>
                    <v-btn text color="primary" @click="bulkSetDescription" :disabled="!bulkDescription">Applica</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
    </div>
</template>

<script>
import VatCodeDialog from '@/components/modules/invoices/edocuments/VatCodeDialog'

export default {
    name: 'EdocumentImportWizard',

    components: { VatCodeDialog },

    props: {
        id: {
            type: Number,
            required: true
        }
    },

    data() {
        return {
            importing: false,
            step: 1,
            previewData: {},

            // Step 1 - Fornitore
            selectedSupplier: null,
            supplierInitialItems: [],
            showCreateSupplierDialog: false,
            creatingSupplier: false,
            newSupplier: {},

            // Step 2 - Prodotti
            productLines: [],
            showLineDetailsDialog: false,
            editingLine: null,
            editingLineIndex: null,

            // Bulk selection
            selectedLineIndices: [],
            showBulkVatDialog: false,
            showBulkProductDialog: false,
            showBulkDescriptionDialog: false,
            bulkVat: null,
            bulkProduct: null,
            bulkDescription: '',

            // Step 3 - Conferma (dynamic fields)
            fieldsset: null,
            form: {},

            // VAT codes
            vatCodeOptions: [],


            // PDF Preview
            pdfPanelExpanded: false,
            pdfPanelBeforeProducts: false,
            vatDialog: { open: false, proposal: null, label: '' },
            // il visore PDF si crea a pannello aperto: durante lo slide calcolerebbe
            // lo zoom sulla larghezza quasi nulla
            pdfPanelReady: false,
            // condizione proposta quando l'XML non ne riconosce una: del fornitore
            // o di ripiego delle impostazioni ({ value, text, origin })
            proposedPaymentTerm: null,
            loadingPdf: false,
            pdfData: null,
            pdfBlob: null
        }
    },

    computed: {
        supplierSummary() {
            if (this.selectedSupplier) {
                return this.selectedSupplier.text
            }
            return 'Seleziona il fornitore'
        },

        // scadenze del documento ricevuto, in ordine di data
        xmlPayments() {
            const payments = (this.previewData && this.previewData.payments) || []
            return payments.slice().sort((a, b) => String(a.expire_date || '').localeCompare(String(b.expire_date || '')))
        },

        xmlPaymentsTotal() {
            return this.xmlPayments.reduce((total, payment) => total + (Number(payment.expire_amount) || 0), 0)
        },

        autoCreateSupplier() {
            return !this.supplierMatched && !!(this.previewData && this.previewData.import_defaults && this.previewData.import_defaults.auto_create_supplier)
        },

        productsSummary() {
            const matched = this.productLines.filter(l => l.product).length
            return `${matched}/${this.productLines.length} prodotti associati`
        },

        isProductsStepValid() {
            return this.productLines.every(l =>
                // Righe solo descrizione: niente quantità, prezzo, IVA o prodotto
                // da scegliere, ma la descrizione resta obbligatoria (il campo e'
                // modificabile e viene segnato in rosso se vuoto)
                (l.is_description && l.name && l.name.trim() !== '') || (
                    !l.is_description &&
                    l.name &&
                    l.name.trim() !== '' &&
                    l.quantity > 0 &&
                    l.unit_price !== null && l.unit_price !== undefined &&
                    l.vat &&
                    l.vat.trim() !== '' &&
                    l.product
                )
            )
        },

        // Get lines with validation errors for visual feedback
        linesWithErrors() {
            return this.productLines.map((line, index) => {
                if (line.is_description) {
                    const descriptionErrors = (!line.name || line.name.trim() === '') ? ['descrizione'] : []
                    return { index, errors: descriptionErrors, hasErrors: descriptionErrors.length > 0 }
                }
                const errors = []
                if (!line.name || line.name.trim() === '') errors.push('descrizione')
                if (!line.quantity || line.quantity <= 0) errors.push('quantità')
                if (line.unit_price === null || line.unit_price === undefined) errors.push('prezzo')
                if (!line.vat || line.vat.trim() === '') errors.push('IVA')
                if (!line.product) errors.push('prodotto')
                return { index, errors, hasErrors: errors.length > 0 }
            })
        },

        linesWithErrorsCount() {
            return this.linesWithErrors.filter(l => l.hasErrors).length
        },

        isConfirmStepValid() {
            if (!this.fieldsset) return false
            const fields = this.fieldsset.fields || this.fieldsset
            if (!Array.isArray(fields)) return false
            return fields.every(f => {
                if (!f.required) return true
                const val = this.form[f.key]
                return val !== null && val !== undefined && val !== ''
            })
        },

        summaryFields() {
            if (!this.fieldsset) return []
            const fields = this.fieldsset.fields || this.fieldsset
            if (!Array.isArray(fields)) return []
            return fields.filter(f => this.form[f.key] !== null && this.form[f.key] !== undefined && this.form[f.key] !== '')
        },

        calculatedTotal() {
            return this.productLines.reduce((sum, line) => sum + this.lineTotal(line), 0)
        },

        // Safe accessors for nested objects
        getTypeInfoName() {
            return (this.previewData.type_info && this.previewData.type_info.name) || 'Tipo documento'
        },

        getTypeInfoTarget() {
            return (this.previewData.type_info && this.previewData.type_info.target) || ''
        },

        edocumentNumber() {
            return (this.previewData.edocument && this.previewData.edocument.number) || ''
        },

        edocumentDate() {
            return (this.previewData.edocument && this.previewData.edocument.date) || ''
        },

        edocumentType() {
            return (this.previewData.edocument && this.previewData.edocument.type) || ''
        },

        edocumentTotal() {
            return (this.previewData.edocument && this.previewData.edocument.total) || 0
        },

        supplierFromEdocument() {
            return (this.previewData.supplier && this.previewData.supplier.from_edocument) || {}
        },

        supplierMatched() {
            return (this.previewData.supplier && this.previewData.supplier.matched) || null
        },

        selectedSupplierName() {
            return (this.selectedSupplier && this.selectedSupplier.text) || ''
        },

        allLinesSelected() {
            return this.productLines.length > 0 && this.selectedLineIndices.length === this.productLines.length
        },

        // aliquote/nature dell'XML senza codice IVA, raggruppate
        unresolvedVats() {
            const pairs = {}
            this.productLines.filter(line => line.vat_unresolved && !line.vat).forEach(line => {
                if(!pairs[line.vat_label]) {
                    pairs[line.vat_label] = { label: line.vat_label, proposal: line.vat_proposal, lines: 0 }
                }
                pairs[line.vat_label].lines++
            })
            return Object.values(pairs)
        },

        // origine della condizione di pagamento: riconosciuta dalle scadenze
        // dell'XML, di ripiego dalle impostazioni dell'import o scelta a mano
        paymentTermOrigin() {
            const value = this.form.payment_term
            if (!value) return 'none'
            const inferred = this.previewData.inferred_payment_term
            if (inferred && inferred.value === value) return 'inferred'
            const proposed = this.proposedPaymentTerm
            if (proposed && proposed.value === value) return proposed.origin === 'supplier' ? 'supplier' : 'default'
            return 'manual'
        },

        paymentTermStatus() {
            if (!this.previewData || !this.fieldsset) return null
            const hasRates = this.xmlPayments.length > 0
            const inferred = this.previewData.inferred_payment_term
            const manualNote = 'Le rate vengono create dall\'XML e risultano modificate a mano se la condizione non le riproduce.'
            switch (this.paymentTermOrigin) {
            case 'inferred':
                return { type: 'success', hint: 'Riconosciuta dalle scadenze dell\'XML', text: 'Condizione di pagamento riconosciuta dalle scadenze dell\'XML: le rate coincidono.' }
            case 'supplier':
                return hasRates
                    ? { type: 'warning', hint: 'Del fornitore: non riconosciuta dalle scadenze dell\'XML', text: 'Nessuna condizione riproduce le scadenze dell\'XML: proposta quella del fornitore. ' + manualNote }
                    : { type: 'info', hint: 'Del fornitore', text: 'L\'XML non contiene scadenze: proposta la condizione del fornitore, le rate vengono calcolate da questa.' }
            case 'default':
                return hasRates
                    ? { type: 'warning', hint: 'Di ripiego: non riconosciuta dalle scadenze dell\'XML', text: 'Nessuna condizione riproduce le scadenze dell\'XML: proposta quella di ripiego delle impostazioni dell\'import. ' + manualNote }
                    : { type: 'info', hint: 'Di ripiego dalle impostazioni dell\'import', text: 'L\'XML non contiene scadenze: proposta la condizione di ripiego delle impostazioni dell\'import, le rate vengono calcolate da questa.' }
            case 'manual':
                if (!hasRates) return { type: 'info', hint: 'Scelta a mano', text: 'Condizione scelta a mano: l\'XML non contiene scadenze, le rate vengono calcolate da questa.' }
                return inferred
                    ? { type: 'warning', hint: 'Scelta a mano', text: 'Condizione scelta a mano, diversa da quella riconosciuta dalle scadenze dell\'XML (' + inferred.text + '). ' + manualNote }
                    : { type: 'info', hint: 'Scelta a mano', text: 'Condizione scelta a mano. ' + manualNote }
            default:
                return hasRates
                    ? { type: 'warning', hint: '', text: 'Nessuna condizione riproduce le scadenze dell\'XML: sceglila guardando le rate qui sotto.' }
                    : null
            }
        },

        someLinesSelected() {
            return this.selectedLineIndices.length > 0
        }
    },

    async mounted() {
        await this.loadPreview()
        // Expand PDF panel and start loading after main data is ready
        this.$nextTick(() => {
            this.pdfPanelExpanded = true
            this.loadPdfPreview()
        })
    },

    watch: {
        // Anteprima PDF chiusa nello step Prodotti, dove la tabella righe ha bisogno
        // di tutta la larghezza; uscendo dallo step torna com'era prima
        step(newStep, oldStep) {
            if (newStep === 2 && oldStep !== 2) {
                this.pdfPanelBeforeProducts = this.pdfPanelExpanded
                this.pdfPanelExpanded = false
            } else if (oldStep === 2 && newStep !== 2 && this.pdfPanelBeforeProducts) {
                this.pdfPanelExpanded = true
            }
        },

        // l'origine della condizione compare anche come hint sotto il campo
        paymentTermStatus(status) {
            if (!this.fieldsset) return
            const fields = this.fieldsset.fields || this.fieldsset
            const field = Array.isArray(fields) && fields.find(f => f.key === 'payment_term')
            if (!field) return
            this.$set(field, 'hint', status ? status.hint : '')
            this.$set(field, 'persistent_hint', !!(status && status.hint))
        },

        // cambiando sezionale nello step di conferma si riapplica la cascata
        'form.sectional_id'() {
            if (this.step === 3) {
                this.refreshLayoutgroup()
            }
        },
    },

    methods: {
        async loadPreview() {
            try {
                const response = await this.axios.get(`edocuments/${this.id}/import-preview`)
                this.previewData = response.data.data

                if (this.previewData.importable) {
                    this.initializeFromPreview()
                }
            } catch (error) {
                console.error('Error loading preview:', error)
                const msg = (error.response && error.response.data && error.response.data.message)
                    || 'Errore nel caricamento del documento'
                this.$store.commit('setSnackbar', {
                    text: msg,
                    value: true,
                    color: 'warning',
                    timeout: 6000
                })
                this.goBack()
            } finally {
                this.$store.commit('endProgress')
            }
        },

        initializeFromPreview() {
            // Initialize supplier
            if (this.supplierMatched) {
                const m = this.supplierMatched
                const mapped = {
                    value: m.id,
                    text: m.business_name,
                    subtitle: [m.vat_number, m.fiscal_code].filter(Boolean).join(' - ')
                }
                this.selectedSupplier = mapped
                this.supplierInitialItems = [mapped]
            }

            // Initialize new supplier form with edocument data
            const fromXml = this.supplierFromEdocument || {}
            this.newSupplier = {
                business_name: fromXml.business_name || '',
                vat_number: fromXml.vat_number || '',
                fiscal_code: fromXml.fiscal_code || '',
                legal_address_street: fromXml.legal_address_street || '',
                legal_address_zipcode: fromXml.legal_address_zipcode || '',
                legal_address_city: fromXml.legal_address_city || '',
                province_id: fromXml.province_id || null,
                province_initial: fromXml.province_initial || '',
                primary_telephone: fromXml.primary_telephone || '',
                primary_email: fromXml.primary_email || '',
                // campi obbligatori proposti dal backend: condizione delle
                // impostazioni dell'import e zona IVA dalla nazione della sede
                payment_term: fromXml.payment_term || null,
                country_id: fromXml.country_id || null,
                legal_address_country: fromXml.country || null,
                vat_zone: fromXml.vat_zone || null,
            }

            // Initialize product lines
            this.productLines = this.previewData.lines.map((line, index) => ({
                id: line.id,
                line_id: line.id,
                line_number: line.line_number || (index + 1),
                name: line.description,
                // Riga solo descrizione: niente quantità/prezzo/IVA, prodotto
                // fisso sul prodotto Descrizione del tenant (non modificabile).
                quantity: line.is_description ? 1 : (parseFloat(line.quantity) || 1),
                unit: line.unit,
                unit_price: line.is_description ? 0 : (parseFloat(line.unit_price) || 0),
                vat: line.is_description ? '' : ((line.matched_vat_code && line.matched_vat_code.key) || ''),
                // aliquota/natura ricevute senza codice IVA corrispondente
                vat_unresolved: !!line.vat_unresolved,
                vat_label: line.vat_label,
                vat_proposal: line.vat_proposal || null,
                product: line.is_description ? this.previewData.description_product : (line.matched_product || null),
                product_code: line.product_code,
                product_code_type: line.product_code_type,
                description: line.description,
                // impostazione "Righe senza prodotto: crea prodotto"
                create_product: !!line.create_product,
                create_product_supplier: false,
                is_description: !!line.is_description,
                // sconti dell'XML come colonne discount1..5: valgono tutti,
                // anche oltre le colonne sconto configurate nel tenant
                ...this.lineDiscounts(line.is_description ? {} : line.discount_columns)
            }))

            // Initialize VAT codes
            this.vatCodeOptions = this.previewData.available_vat_codes || []

            // Load FieldsGenerator fields from backend
            if (this.previewData.fields) {
                this.fieldsset = this.previewData.fields
            }

            // Pre-fill date from edocument
            this.form.date = this.edocumentDate || new Date().toISOString().substr(0, 10)

            // Condizione di pagamento: dedotta dalle scadenze dell'XML, altrimenti
            // quella del fornitore abbinato o di ripiego delle impostazioni
            const defaults = this.previewData.import_defaults || {}
            this.proposedPaymentTerm = defaults.payment_term || null
            const term = this.previewData.inferred_payment_term || defaults.payment_term
            if (term && !this.form.payment_term) {
                this.$set(this.form, 'payment_term', term.value)
            }
            // Sezionale delle impostazioni dell'import per questo tipo di documento
            if (defaults.sectional_id && !this.form.sectional_id) {
                this.$set(this.form, 'sectional_id', defaults.sectional_id)
            }
        },

        applyDefaults() {
            const fields = this.fieldsset.fields || this.fieldsset
            if (Array.isArray(fields)) {
                fields.forEach(field => {
                    if (field.default && !this.form[field.key]) {
                        this.$set(this.form, field.key, field.default)
                    }
                })
            }
        },

        getSummaryValue(field) {
            const value = this.form[field.key]
            if (value === null || value === undefined || value === '') return '-'
            if (field.type === 'date') return this.formatDate(value)
            if (field.options) {
                const option = field.options.find(o => o.value == value)
                return option ? option.text : value
            }
            return value
        },

        getFieldLabel(fieldKey, value) {
            if (!this.fieldsset || !value) return value || ''
            const fields = this.fieldsset.fields || this.fieldsset
            if (!Array.isArray(fields)) return value
            const field = fields.find(f => f.key === fieldKey)
            if (!field || !field.options) return value
            const option = field.options.find(o => o.value == value)
            return option ? option.text : value
        },

        async createSupplier() {
            this.creatingSupplier = true
            try {
                // Usa l'endpoint esistente per creare fornitori
                const response = await this.axios.post('suppliers', this.newSupplier)
                // L'endpoint restituisce { id, option: { value, text, icon } }
                const newSupplier = {
                    value: response.data.id,
                    text: response.data.option.text,
                    subtitle: [this.newSupplier.vat_number, this.newSupplier.fiscal_code].filter(Boolean).join(' - ')
                }

                this.selectedSupplier = newSupplier
                this.supplierInitialItems = [newSupplier]
                this.showCreateSupplierDialog = false

                this.$store.commit('setSnackbar', {
                    text: 'Fornitore creato con successo',
                    value: true,
                    color: 'success',
                    timeout: 6000
                })
            } catch (error) {
                console.error('Error creating supplier:', error)
                this.$store.commit('setSnackbar', {
                    text: 'Errore nella creazione del Fornitore',
                    value: true,
                    color: 'error',
                    timeout: 6000
                })
            } finally {
                this.creatingSupplier = false
            }
        },

        paymentMethodLabel(payment) {
            if (payment.matched_payment_method && payment.matched_payment_method.text) {
                return payment.matched_payment_method.text + (payment.payment_method ? ' (' + payment.payment_method + ')' : '')
            }
            if (payment.payment_method_label) {
                return payment.payment_method_label + (payment.payment_method ? ' (' + payment.payment_method + ')' : '')
            }
            return payment.payment_method || '—'
        },

        openLineDetails(index) {
            if (this.productLines[index] && this.productLines[index].is_description) return
            this.editingLineIndex = index
            this.editingLine = { ...this.productLines[index] }
            this.showLineDetailsDialog = true
        },

        closeLineDetails() {
            if (this.editingLineIndex !== null) {
                this.$set(this.productLines, this.editingLineIndex, { ...this.editingLine })
            }
            this.showLineDetailsDialog = false
        },

        // Bulk selection methods
        isLineSelected(index) {
            return this.selectedLineIndices.includes(index)
        },

        toggleLineSelection(index) {
            const idx = this.selectedLineIndices.indexOf(index)
            if (idx > -1) {
                this.selectedLineIndices.splice(idx, 1)
            } else {
                this.selectedLineIndices.push(index)
            }
        },

        toggleSelectAll(value) {
            if (value) {
                this.selectedLineIndices = this.productLines.map((_, index) => index)
            } else {
                this.selectedLineIndices = []
            }
        },

        // Bulk action methods
        // righe selezionate divise tra righe prodotto e righe descrittive: le
        // azioni su prodotto e IVA non toccano le descrittive (al salvataggio
        // restano sul prodotto Descrizione, senza IVA)
        splitSelectedLines() {
            const productIndices = this.selectedLineIndices.filter(index => !this.productLines[index].is_description)
            return { productIndices, skipped: this.selectedLineIndices.length - productIndices.length }
        },

        bulkResultText(action, count, skipped) {
            const base = `${action} su ${count} ${count === 1 ? 'riga' : 'righe'}`
            return skipped > 0 ? `${base}, ${skipped} ${skipped === 1 ? 'riga descrittiva esclusa' : 'righe descrittive escluse'}` : base
        },

        applyBulkVat() {
            if (!this.bulkVat) return

            const { productIndices, skipped } = this.splitSelectedLines()
            productIndices.forEach(index => {
                this.$set(this.productLines[index], 'vat', this.bulkVat)
            })
            this.selectedLineIndices = []
            this.showBulkVatDialog = false
            this.bulkVat = null
            this.$store.commit('setSnackbar', {
                text: this.bulkResultText('IVA impostata', productIndices.length, skipped),
                value: true,
                color: 'success',
                timeout: 6000
            })
        },

        bulkSetProduct() {
            if (!this.bulkProduct) return

            const { productIndices, skipped } = this.splitSelectedLines()
            productIndices.forEach(index => {
                this.$set(this.productLines[index], 'product', { ...this.bulkProduct })
            })
            this.selectedLineIndices = []
            this.showBulkProductDialog = false
            this.bulkProduct = null
            this.$store.commit('setSnackbar', {
                text: this.bulkResultText('Prodotto impostato', productIndices.length, skipped),
                value: true,
                color: 'success',
                timeout: 6000
            })
        },

        bulkSetDescription() {
            if (!this.bulkDescription) return

            this.selectedLineIndices.forEach(index => {
                this.$set(this.productLines[index], 'name', this.bulkDescription)
            })
            this.selectedLineIndices = []
            this.showBulkDescriptionDialog = false
            this.bulkDescription = ''
            this.$store.commit('setSnackbar', {
                text: 'Descrizione impostata per le righe selezionate',
                value: true,
                color: 'success',
                timeout: 6000
            })
        },

        bulkToggleProductSupplier() {
            let count = 0
            this.selectedLineIndices.forEach(index => {
                if (this.productLines[index].product && !this.productLines[index].is_description) {
                    this.$set(this.productLines[index], 'create_product_supplier', true)
                    count++
                }
            })
            this.selectedLineIndices = []
            this.$store.commit('setSnackbar', {
                text: count > 0
                    ? `Associazione prodotto-fornitore attivata per ${count} righe`
                    : 'Nessuna riga selezionata ha un prodotto associato',
                value: true,
                color: count > 0 ? 'success' : 'warning',
                timeout: 6000
            })
        },

        hasLineErrors(index) {
            const line = this.productLines[index]
            if (!line || line.is_description) return false
            return (
                !line.name || line.name.trim() === '' ||
                !line.quantity || line.quantity <= 0 ||
                line.unit_price === null || line.unit_price === undefined ||
                !line.vat || line.vat.trim() === '' ||
                !line.product
            )
        },

        async nextStep(currentStep) {
            // impostazione "Crea automaticamente i fornitori mancanti"
            if (currentStep === 1 && !this.selectedSupplier && this.autoCreateSupplier) {
                await this.createSupplier()
            }
            if (currentStep === 1 && this.selectedSupplier) {
                this.step = 2
            } else if (currentStep === 2 && this.isProductsStepValid) {
                this.step = 3
                this.refreshLayoutgroup()
            }
        },

        // Gruppo layout con la cascata delle vendite, calcolata dal backend:
        // predefinito generale, poi sezionale, poi fornitore
        // valori proposti per fornitore e sezionale scelti: gruppo layout e, se
        // l'XML non riconosce la condizione, quella del fornitore o delle impostazioni
        async refreshLayoutgroup() {
            if (!this.selectedSupplier) return
            try {
                const response = await this.axios.get(`edocuments/${this.id}/import-defaults`, {
                    params: { supplier_id: this.selectedSupplier.value, sectional_id: this.form.sectional_id || null }
                })
                if (response.data && response.data.layoutgroup_id) {
                    this.$set(this.form, 'layoutgroup_id', response.data.layoutgroup_id)
                }
                this.applyProposedPaymentTerm(response.data ? response.data.payment_term : null)
            } catch (error) {
                // senza proposta resta la scelta manuale (il campo e' obbligatorio)
            }
        },

        // la proposta sostituisce solo un valore non scelto a mano
        applyProposedPaymentTerm(term) {
            if (this.previewData.inferred_payment_term) return
            const current = this.form.payment_term
            const untouched = !current || (this.proposedPaymentTerm && this.proposedPaymentTerm.value === current)
            this.proposedPaymentTerm = term || null
            if (untouched) {
                this.$set(this.form, 'payment_term', term ? term.value : null)
            }
        },

        openVatDialog(pair) {
            this.vatDialog = { open: true, proposal: Object.assign({}, pair.proposal), label: pair.label }
        },

        // codice appena creato: entra nella tendina e copre le righe di quella coppia
        applyCreatedVat(vat) {
            const label = this.vatDialog.label
            this.vatCodeOptions = this.vatCodeOptions.concat([{ key: vat.value, text: vat.text }])
            this.productLines.forEach((line, index) => {
                if(line.vat_label === label && line.vat_unresolved && !line.vat) {
                    this.$set(this.productLines, index, Object.assign({}, line, { vat: vat.value, vat_unresolved: false }))
                }
            })
        },

        async executeImport() {
            this.importing = true
            try {
                const payload = {
                    supplier_id: this.selectedSupplier.value,
                    sectional_id: this.form.sectional_id,
                    date: this.form.date,
                    subtype: this.form.subtype || null,
                    warehouse_id: this.form.warehouse_id || null,
                    layoutgroup_id: this.form.layoutgroup_id || null,
                    payment_term: this.form.payment_term || null,
                    notes: this.form.notes || null,
                    project_id: this.form.project_id || null,
                    products: this.productLines.map(line => ({
                        line_id: line.line_id,
                        product_id: (line.product && line.product.value) || null,
                        name: line.name,
                        quantity: line.quantity,
                        unit_price: line.unit_price,
                        vat: line.vat,
                        unit: line.unit,
                        description: line.description,
                        product_code: line.product_code,
                        create_product: line.create_product,
                        create_product_supplier: line.create_product_supplier,
                        warehouse_id: this.form.warehouse_id || null,
                        is_description: !!line.is_description,
                        ...this.lineDiscounts(line)
                    }))
                }

                // Add force_reimport if document was already imported
                if (this.previewData.already_imported) {
                    payload.force_reimport = true
                }

                const response = await this.axios.post(`edocuments/${this.id}/import`, payload)

                this.$store.commit('setSnackbar', {
                    text: 'Documento importato con successo',
                    value: true,
                    color: 'success',
                    timeout: 6000
                })

                if (response.data.warnings && response.data.warnings.length) {
                    setTimeout(() => {
                        this.$store.commit('setSnackbar', {
                            text: response.data.warnings.join(' — '),
                            value: true,
                            color: 'warning',
                            timeout: 8000
                        })
                    }, 6500)
                }

                // Redirect to the created document
                const docType = response.data.document_type
                const docId = response.data.document_id
                this.$router.push(`/invoices/${docType}s/${docId}/`)

            } catch (error) {
                console.error('Error importing document:', error)
                if (error.response && error.response.data && error.response.data.code === 'previous_has_payments') {
                    this.$store.commit('setSnackbar', {
                        text: error.response.data.message,
                        value: true,
                        color: 'error',
                        timeout: 10000
                    })
                } else {
                    const errorMessage = (error.response && error.response.data && error.response.data.message) || 'Errore nell\'importazione'
                    this.$store.commit('setSnackbar', {
                        text: errorMessage,
                        value: true,
                        color: 'error',
                        timeout: 6000
                    })
                }
            } finally {
                this.importing = false
            }
        },

        goBack() {
            this.$router.push(`/invoices/edocuments/${this.id}/`)
        },

        formatDate(date) {
            if (!date) return ''
            return new Date(date).toLocaleDateString('it-IT')
        },

        formatCurrency(value) {
            if (value === null || value === undefined) return '€ 0,00'
            return new Intl.NumberFormat('it-IT', {
                style: 'currency',
                currency: 'EUR'
            }).format(value)
        },

        // colonne discount1..5 di una riga (o delle discount_columns dell'anteprima)
        lineDiscounts(source) {
            const discounts = {}
            for (let i = 1; i <= 5; i++) {
                discounts['discount' + i] = (source && parseFloat(source['discount' + i])) || 0
            }
            return discounts
        },

        // quantita' per prezzo scontato in cascata, come il calcolo del documento
        lineTotal(line) {
            if (line.is_description) return 0
            let price = parseFloat(line.unit_price) || 0
            for (let i = 1; i <= 5; i++) {
                const discount = parseFloat(line['discount' + i]) || 0
                if (discount) price = price * (100 - discount) / 100
            }
            return (parseFloat(line.quantity) || 0) * price
        },

        // es. "30% + 7%"; una maggiorazione (sconto negativo) come "+10%"
        discountLabel(line) {
            const labels = []
            for (let i = 1; i <= 5; i++) {
                const discount = parseFloat(line['discount' + i]) || 0
                if (!discount) continue
                const value = Math.abs(discount).toLocaleString('it-IT', { maximumFractionDigits: 2 })
                labels.push((discount < 0 ? '+' : '') + value + '%')
            }
            return labels.join(' + ')
        },

        getTypeColor(target) {
            switch (target) {
                case 'purchaseinvoice': return 'primary'
                case 'suppliercreditnote': return 'warning'
                default: return 'grey'
            }
        },

        getTargetModelName() {
            switch (this.previewData.target_model) {
                case 'purchaseinvoice': return 'Fattura di Acquisto'
                case 'suppliercreditnote': return 'Nota di Credito da Fornitore'
                default: return this.previewData.target_model
            }
        },

        getPreviousImportTypeName() {
            if (!this.previewData.previous_import) return ''
            switch (this.previewData.previous_import.document_type) {
                case 'purchaseinvoice': return 'Fattura di Acquisto'
                case 'suppliercreditnote': return 'Nota di Credito da Fornitore'
                default: return this.previewData.previous_import.document_type
            }
        },

        goToPreviousImport() {
            if (!this.previewData.previous_import) return
            const docType = this.previewData.previous_import.document_type
            const docId = this.previewData.previous_import.document_id
            if (docType && docId) {
                this.$router.push(`/invoices/${docType}s/${docId}/`)
            }
        },

        getVatLabel(vatKey) {
            const vat = this.vatCodeOptions.find(v => v.key === vatKey)
            return vat ? vat.text : vatKey
        },

        async loadPdfPreview() {
            this.loadingPdf = true
            try {
                const options = {
                    headers: {
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'Authorization': 'Bearer ' + this.$store.getters.token
                    },
                    method: 'GET'
                }
                const response = await fetch(
                    this.$store.getters.baseUrl + '/api/edocuments/e-invoice/' + this.id + '/print',
                    options
                )
                if (!response.ok) {
                    throw new Error('Errore nel caricamento del PDF')
                }
                const blob = await response.blob()
                this.pdfBlob = new Blob([blob], { type: blob.type })
                this.pdfData = window.URL.createObjectURL(this.pdfBlob)
            } catch (error) {
                console.error('Error loading PDF preview:', error)
            } finally {
                this.loadingPdf = false
            }
        },

        downloadPdf() {
            if (this.pdfBlob) {
                const link = document.createElement('a')
                const url = window.URL.createObjectURL(this.pdfBlob)
                link.href = url
                link.setAttribute('target', '_blank')
                link.download = 'Documento_' + this.id + '.pdf'
                link.click()
                setTimeout(() => {
                    window.URL.revokeObjectURL(url)
                }, 100)
            }
        },

        togglePdfPanel() {
            this.pdfPanelExpanded = !this.pdfPanelExpanded
        }
    },

    beforeDestroy() {
        if (this.pdfData) {
            window.URL.revokeObjectURL(this.pdfData)
        }
    }
}
</script>

<style scoped>
/* tabella righe dello step Prodotti: larghezze minime e scorrimento orizzontale */
.lines-table >>> table {
    min-width: 1180px;
}
.lines-table >>> th.col-description {
    min-width: 280px;
}
.lines-table >>> th.col-quantity {
    min-width: 100px;
}
.lines-table >>> th.col-price {
    min-width: 130px;
}
.lines-table >>> th.col-vat {
    min-width: 190px;
}
.lines-table >>> th.col-product {
    min-width: 300px;
}

.import-wizard-container {
    width: 100%;
}

.pb-offset {
    padding-bottom: 80px;
}

.wizard-column {
    transition: all 0.3s ease;
}

.wizard-column.with-pdf-panel {
    max-width: calc(100% - 41.666667%); /* lg="5" equivalent */
}

@media (max-width: 1263px) {
    .wizard-column.with-pdf-panel {
        max-width: 100%;
    }
}

.pdf-panel-col {
    overflow: hidden;
}

.pdf-preview-card {
    position: sticky;
    top: 80px;
}

.pdf-object {
    width: 100%;
    height: calc(100vh - 200px);
    min-height: 600px;
}

/* Slide transition for PDF panel */
.slide-pdf-enter-active,
.slide-pdf-leave-active {
    transition: all 0.3s ease;
}

.slide-pdf-enter,
.slide-pdf-leave-to {
    transform: translateX(100%);
    opacity: 0;
    max-width: 0;
    padding: 0 !important;
}

@media (max-width: 1263px) {
    .pdf-preview-card {
        position: relative;
        top: 0;
    }

    .pdf-object {
        height: 500px;
        min-height: 400px;
    }
}

/* Selected row highlight */
.selected-row {
    background-color: rgba(var(--v-theme-primary), 0.08) !important;
}

.theme--dark .selected-row {
    background-color: rgba(25, 118, 210, 0.16) !important;
}

/* Error row highlight */
.error-row {
    background-color: rgba(255, 82, 82, 0.08) !important;
}

.theme--dark .error-row {
    background-color: rgba(255, 82, 82, 0.16) !important;
}

/* Error row takes precedence over selected */
.error-row.selected-row {
    background-color: rgba(255, 82, 82, 0.15) !important;
}

.theme--dark .error-row.selected-row {
    background-color: rgba(255, 82, 82, 0.25) !important;
}
</style>
